import React, { useState, useEffect } from 'react';
import { FileText, Download, Search, RefreshCw, ExternalLink, Shield, Eye, Brain, Trash2 } from 'lucide-react';
import { notify } from '@core/notifications/notify';
import PageInfoButton from '@/components/PageInfoButton';
import ConfirmationModal from '@/components/ConfirmationModal';

interface ParsedDoc {
  id: number;
  title: string;
  original_filename: string;
  file_size: number;
  document_type: string | null;
  classification_confidence: number | null;
  page_count: number | null;
  framework_id: number | null;
  template_id: number | null;
  draft_id: number | null;
  status: string;
  created_at: string;
  extracted_text: string;
}

const DocumentLibrary: React.FC = () => {
  const [docs, setDocs] = useState<ParsedDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDoc, setSelectedDoc] = useState<ParsedDoc | null>(null);
  const [fullText, setFullText] = useState<string | null>(null);
  const [loadingText, setLoadingText] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ParsedDoc | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadDocs(); }, []);

  const loadDocs = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/parser/documents', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) {
        const json = await res.json();
        setDocs(json.data || []);
      }
    } catch {
      notify.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const viewDocument = async (doc: ParsedDoc) => {
    setSelectedDoc(doc);
    setLoadingText(true);
    try {
      const res = await fetch(`/api/parser/documents/${doc.id}/text`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) {
        const json = await res.json();
        setFullText(json.data?.text || '');
      }
    } catch {
      setFullText('Failed to load document text.');
    } finally {
      setLoadingText(false);
    }
  };

  const getCsrfToken = () =>
    document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/parser/documents/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-CSRF-Token': getCsrfToken(),
        },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json?.success) {
        const d = json.data || {};
        const parts: string[] = [];
        if (d.templateId) parts.push(`template ${d.templateId}`);
        if (d.frameworkVersionId) parts.push(`version ${d.frameworkVersionId}`);
        if (d.nodes) parts.push(`${d.nodes} nodes`);
        if (d.fields) parts.push(`${d.fields} fields`);
        notify.success(parts.length ? `Deleted: ${parts.join(', ')}` : 'Document deleted');
        setDocs((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      } else {
        notify.error(json?.message || 'Delete failed');
      }
    } catch (err: any) {
      notify.error(err?.message || 'Delete failed');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const filtered = docs.filter(d =>
    !search || d.title.toLowerCase().includes(search.toLowerCase()) ||
    d.original_filename.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={32} />
      </div>
    );
  }

  // Full text viewer
  if (selectedDoc && fullText !== null) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <button onClick={() => { setSelectedDoc(null); setFullText(null); }} className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 mb-4">
            &larr; Back to Document Library
          </button>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h1 className="text-xl font-bold text-slate-900 text-left">{selectedDoc.title}</h1>
              <p className="text-sm text-slate-500 text-left">{selectedDoc.original_filename} — {selectedDoc.page_count || '?'} pages — Uploaded {new Date(selectedDoc.created_at).toLocaleDateString()}</p>
            </div>
            <div className="p-6">
              {loadingText ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="text-blue-600 animate-spin" size={24} />
                </div>
              ) : (
                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-mono leading-relaxed max-h-[70vh] overflow-y-auto text-left">
                  {fullText}
                </pre>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900 text-left">Document Library</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm mt-0.5 text-left">Parsed compliance documents — AI assistant can reference these when editing templates</p>
          </div>
          <button onClick={loadDocs} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Search documents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
          />
        </div>

        {/* Documents list */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <FileText className="text-slate-300 mx-auto mb-4" size={40} />
            <h3 className="text-lg font-bold text-slate-800 mb-2">No Documents Yet</h3>
            <p className="text-sm text-slate-500">Upload a compliance standard PDF through the AI assistant chat to see it here.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(doc => (
              <div key={doc.id} className="bg-white rounded-xl border border-slate-200 p-4 hover:border-blue-200 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate text-left">{doc.title}</p>
                    <p className="text-xs text-slate-500 text-left">
                      {doc.original_filename} — {(doc.file_size / 1024).toFixed(0)} KB — {doc.page_count || '?'} pages —
                      {doc.document_type ? ` ${doc.document_type.replace(/_/g, ' ')}` : ''} —
                      {doc.classification_confidence ? ` ${Math.round(doc.classification_confidence * 100)}% confidence` : ''} —
                      {new Date(doc.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {doc.status === 'framework_created' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200">
                        <Shield size={10} /> Framework Created
                      </span>
                    )}
                    {doc.draft_id && (
                      <a
                        href={`/compliance/definition/parser?draftId=${doc.draft_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors"
                      >
                        <Brain size={13} /> Edit Structure
                      </a>
                    )}
                    <button
                      onClick={() => viewDocument(doc)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                    >
                      <Eye size={13} /> View Text
                    </button>
                    {doc.template_id && (
                      <a
                        href={`/compliance/definition/builder?templateId=${doc.template_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
                        title="Open the template this document was parsed into. If the template has since been deleted you'll be redirected to the template list."
                      >
                        <ExternalLink size={13} /> Open Template
                      </a>
                    )}
                    <button
                      onClick={() => setDeleteTarget(doc)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors"
                      title="Permanently delete this document, its template, and its framework version. Refuses if downstream data exists."
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
                {doc.extracted_text && (
                  <p className="text-xs text-slate-400 mt-2 truncate text-left">{doc.extracted_text}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        onConfirm={confirmDelete}
        title="Delete parsed document and its template?"
        message={
          deleteTarget
            ? `This will permanently delete "${deleteTarget.title}" from the library and cascade-remove its linked template${
                deleteTarget.template_id ? ` (id ${deleteTarget.template_id})` : ''
              }, the framework version bound to it, all template nodes and fields, and the ingestion log. The framework itself is preserved so future parses can reuse it. The server will refuse if any partner assignments, compliance pages, certifications, or other downstream data exist for this template/version.`
            : ''
        }
        confirmText={deleting ? 'Deleting…' : 'Delete permanently'}
        cancelText="Cancel"
        type="danger"
        loading={deleting}
      />
    </div>
  );
};

export default DocumentLibrary;
